import { useState } from 'react'
import { kycApi } from '../../../services/api-client'
import type { VendorKYC, DocumentType } from '../../../types'

interface Props {
  kyc: VendorKYC | null
  onComplete: (kyc: VendorKYC) => void
  onBack?: () => void
}

export function Step2Document({ kyc, onComplete, onBack }: Props) {
  const [documentType, setDocumentType] = useState<DocumentType>(kyc?.document_type || 'dni')
  const [documentNumber, setDocumentNumber] = useState(kyc?.document_number || '')
  const [expiryDate, setExpiryDate] = useState(kyc?.document_expiry_date || '')
  const [frontImage, setFrontImage] = useState<File | null>(null)
  const [backImage, setBackImage] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!frontImage) {
      setError('Debe subir la imagen frontal del documento')
      return
    }

    setError(null)
    setLoading(true)

    try {
      const formData = new FormData()
      formData.append('document_type', documentType)
      formData.append('document_number', documentNumber.toUpperCase())
      formData.append('document_expiry_date', expiryDate)
      formData.append('document_front_image', frontImage)
      if (backImage) {
        formData.append('document_back_image', backImage)
      }

      const response = await kycApi.submitStep2(formData)
      onComplete(response.kyc)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al guardar los documentos')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Documentación de Identidad
        </h2>
        <p className="text-gray-600">
          Suba imágenes claras de su documento de identidad oficial.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        <div>
          <label htmlFor="document_type" className="block text-sm font-medium text-gray-700 mb-2">
            Tipo de Documento <span className="text-red-500">*</span>
          </label>
          <select
            id="document_type"
            required
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value as DocumentType)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="dni">DNI / Cédula Nacional</option>
            <option value="passport">Pasaporte</option>
            <option value="nie">NIE (Número de Identidad de Extranjero)</option>
          </select>
        </div>

        <div>
          <label htmlFor="document_number" className="block text-sm font-medium text-gray-700 mb-2">
            Número de Documento <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="document_number"
            required
            value={documentNumber}
            onChange={(e) => setDocumentNumber(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="ABC123456"
          />
        </div>

        <div>
          <label htmlFor="expiry_date" className="block text-sm font-medium text-gray-700 mb-2">
            Fecha de Expiración <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            id="expiry_date"
            required
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="front_image" className="block text-sm font-medium text-gray-700 mb-2">
            Imagen Frontal del Documento <span className="text-red-500">*</span>
          </label>
          <input
            type="file"
            id="front_image"
            required={!kyc?.document_front_image}
            accept="image/*"
            onChange={(e) => setFrontImage(e.target.files?.[0] || null)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-sm text-gray-500 mt-1">
            Formatos aceptados: JPG, PNG. Tamaño máximo: 5MB
          </p>
        </div>

        <div>
          <label htmlFor="back_image" className="block text-sm font-medium text-gray-700 mb-2">
            Imagen Trasera del Documento (opcional)
          </label>
          <input
            type="file"
            id="back_image"
            accept="image/*"
            onChange={(e) => setBackImage(e.target.files?.[0] || null)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="flex justify-between pt-6 border-t">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Atrás
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className={`px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${!onBack ? 'ml-auto' : ''}`}
        >
          {loading ? 'Guardando...' : 'Continuar'}
        </button>
      </div>
    </form>
  )
}
